import React from 'react';
import { AlertCircle, Wifi, Settings, RefreshCw } from 'lucide-react';

interface FallbackCalendarProps {
  error?: Error;
  resetError: () => void;
}

const FallbackCalendar: React.FC<FallbackCalendarProps> = ({
  error,
  resetError,
}) => {
  return (
    <div className="space-y-6">
      {/* System Status - Fallback Mode */}
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-orange-600" />
          <div>
            <h3 className="font-semibold text-orange-900">Fallback Mode</h3>
            <p className="text-sm text-orange-800">
              The Revenue Management System is running in fallback mode. Some
              features may be limited.
            </p>
          </div>
        </div>
      </div>

      {/* System Status Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* WebHotelier Status */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-slate-100">
                  WebHotelier
                </h3>
                <p className="text-sm text-gray-600">Fallback Mode</p>
              </div>
            </div>
            <Wifi className="h-4 w-4 text-gray-400" />
          </div>
        </div>

        {/* Revenue Engine Status */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-slate-100">
                  Revenue Engine
                </h3>
                <p className="text-sm text-gray-600">Disabled</p>
              </div>
            </div>
            <Settings className="h-4 w-4 text-gray-400" />
          </div>
        </div>

        {/* Rate Sync Status */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-slate-100">
                  Rate Sync
                </h3>
                <p className="text-sm text-gray-600">Offline</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Room Types Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-slate-100">
              Room Types
            </h3>
            <p className="text-gray-600 mt-1 text-sm sm:text-base truncate">
              No room types available in fallback mode
            </p>
          </div>
        </div>

        <div className="text-center py-12">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6">
            <AlertCircle className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-3">
            Service Unavailable
          </h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            The Revenue Management System is experiencing issues. Please check
            your configuration or try again later.
          </p>
          <div className="space-y-2 text-sm text-gray-600">
            <p>Possible solutions:</p>
            <ul className="text-left max-w-md mx-auto space-y-1">
              <li>• Check WebHotelier API credentials</li>
              <li>• Verify network connectivity</li>
              <li>• Refresh the page</li>
              <li>• Contact system administrator</li>
            </ul>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 max-w-md mx-auto">
              <p className="text-xs font-medium text-red-800 mb-1">
                Error Details:
              </p>
              <p className="text-xs text-red-700 font-mono break-all">
                {error.message}
              </p>
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={resetError}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>

      {/* Basic Calendar Placeholder */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-4">
          Calendar
        </h3>
        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            Calendar is not available in fallback mode
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Please resolve the system issues to access the full calendar
            functionality
          </p>
        </div>
      </div>
    </div>
  );
};

export default FallbackCalendar;
